/**
 * ApprovalModal — leadership/admin dialog to approve a pending_approval project.
 *
 * Shown from the Projects page "Pending Approval" section. Lets the approver confirm the
 * project name, type, SOW hours, kickoff / expected completion dates and the Project Summary /
 * Business Use Case, and assign the SA / PM / Lead Engineer from the Clockify resource directory
 * (ResourcePicker). On submit it POSTs /api/projects/{id}/approve which flips the project to Active
 * and fires provisioning + template seeding server-side.
 *
 * V057: SOW hours, kickoff and expected completion are REQUIRED — the backend 400s
 * (`details.missing`) without them, so the submit stays disabled until all three are valid.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import type { OscarIntake } from "@/types/intake";
import {
  PROJECT_SUMMARY_MAX,
  intakeProjectSummary,
  requiredApprovalErrors,
  requiredApprovalSummary,
} from "@/types/intake";
// Same endpoint as useApproveProject, but its body type carries the V057 required dates and
// project_summary, and it also invalidates the Intakes list.
import { useApproveIntake } from "@/hooks/useIntakes";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ResourcePicker } from "@/components/resources/ResourcePicker";
import { PROJECT_TYPE_OPTIONS } from "@/lib/project-types";

export interface ApprovalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A pending_approval list row. Oscar intakes carry the V057 AE / summary fields. */
  project: OscarIntake | null;
}

export function ApprovalModal({
  open,
  onOpenChange,
  project,
}: ApprovalModalProps): JSX.Element {
  const approve = useApproveIntake(project?.jira_key ?? "");

  const [title, setTitle] = useState("");
  const [projectType, setProjectType] = useState("AppDev");
  const [sowHours, setSowHours] = useState("");
  const [kickoff, setKickoff] = useState("");
  const [completion, setCompletion] = useState("");
  const [projectSummary, setProjectSummary] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [sa, setSa] = useState({ id: "", name: "" });
  const [pm, setPm] = useState({ id: "", name: "" });
  const [lead, setLead] = useState({ id: "", name: "" });
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form whenever a new project is selected for approval.
  useEffect(() => {
    if (project) {
      setTitle(project.title);
      setProjectType(project.project_type || "AppDev");
      setSowHours(project.sow_hours != null ? String(project.sow_hours) : "");
      setKickoff(project.planned_kickoff_date?.slice(0, 10) ?? "");
      setCompletion(project.expected_completion_date?.slice(0, 10) ?? "");
      setProjectSummary(intakeProjectSummary(project));
      setAttempted(false);
      setSa({ id: "", name: "" });
      setPm({ id: "", name: "" });
      setLead({ id: "", name: "" });
      setError(null);
    }
  }, [project]);

  const requiredErrors = requiredApprovalErrors({
    sowHours,
    kickoff,
    completion,
  });
  const requiredSummary = requiredApprovalSummary(requiredErrors);
  // Inline messages only after the first attempt; the submit is disabled meanwhile.
  const shownErrors = attempted ? requiredErrors : {};

  const handleApprove = async (): Promise<void> => {
    if (!project) return;
    setAttempted(true);
    if (!title.trim()) {
      setError("Project name is required.");
      return;
    }
    if (!sa.name.trim()) {
      setError("A Solutions Architect must be assigned.");
      return;
    }
    if (!pm.name.trim()) {
      setError("A Project Manager must be assigned.");
      return;
    }
    if (requiredSummary) {
      setError(requiredSummary);
      return;
    }
    setError(null);

    try {
      await approve.mutateAsync({
        title: title.trim(),
        solution_architect: sa.name.trim(),
        project_manager: pm.name.trim(),
        ...(lead.name.trim() ? { engineers_assigned: lead.name.trim() } : {}),
        project_type: projectType,
        sow_hours: Number(sowHours),
        planned_kickoff_date: kickoff,
        expected_completion_date: completion,
        // Always sent: the field is prefilled, so an empty value is a deliberate clear.
        project_summary: projectSummary.trim(),
        // The picks as project_resources rows → the team are project members. Only when both
        // required picks carry a resource id (the backend requires one SA + one PM in the array).
        ...(sa.id && pm.id
          ? {
              team_assignments: [
                {
                  resource_id: sa.id,
                  role_on_project: "Solution Architect",
                  capacity_pct: null,
                },
                {
                  resource_id: pm.id,
                  role_on_project: "Project Manager",
                  capacity_pct: null,
                },
                ...(lead.id
                  ? [
                      {
                        resource_id: lead.id,
                        role_on_project: "Lead Implementation Engineer",
                        capacity_pct: null,
                      },
                    ]
                  : []),
              ],
            }
          : {}),
      });
      toast.success("Project approved — provisioning started");
      onOpenChange(false);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      setError(msg || "Failed to approve project.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Approve Project</DialogTitle>
          <DialogDescription>
            Assign the delivery team and confirm the type, SOW hours and dates.
            Approving provisions the GitHub repo, Slack channels, Jira board and
            Clockify project, and seeds the CASDM gate template + meeting set.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="am-title">Project Name *</Label>
            <Input
              id="am-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Project name"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="am-type">Project Type</Label>
              <Select
                id="am-type"
                value={projectType}
                onChange={setProjectType}
                options={PROJECT_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="am-sow">
                SOW Hours <span className="text-error">*</span>
              </Label>
              <Input
                id="am-sow"
                type="number"
                min="1"
                value={sowHours}
                onChange={(e) => setSowHours(e.target.value)}
                placeholder="160"
                required
                aria-required="true"
                error={shownErrors.sowHours}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="am-jira">Jira Project Key</Label>
            {/* Read-only: generated at intake; renaming it is unsupported (23 FK dependents). */}
            <div
              id="am-jira"
              className="flex h-10 items-center rounded-md border bg-subtle-bg/40 px-3 font-mono text-sm text-text-primary"
            >
              {project?.jira_key || "—"}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="am-kickoff">
                Kickoff Date <span className="text-error">*</span>
              </Label>
              <DatePicker
                id="am-kickoff"
                value={kickoff}
                onChange={setKickoff}
                required
                error={shownErrors.kickoff}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="am-completion">
                Expected Completion Date <span className="text-error">*</span>
              </Label>
              <DatePicker
                id="am-completion"
                value={completion}
                onChange={setCompletion}
                min={kickoff || undefined}
                required
                error={shownErrors.completion}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="am-summary">
              Project Summary / Business Use Case
            </Label>
            <p id="am-summary-help" className="text-xs text-text-secondary">
              Purpose, business problem and intended use case
            </p>
            <Textarea
              id="am-summary"
              value={projectSummary}
              onChange={(e) => setProjectSummary(e.target.value)}
              maxLength={PROJECT_SUMMARY_MAX}
              rows={4}
              aria-describedby="am-summary-help am-summary-count"
            />
            <p
              id="am-summary-count"
              className="text-right text-xs text-text-secondary"
            >
              {projectSummary.length.toLocaleString()} /{" "}
              {PROJECT_SUMMARY_MAX.toLocaleString()} characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="am-sa">Solutions Architect *</Label>
            {/*
              SA / PM / Lead Engineer are NAMED LEADS — often senior staff who do not submit
              timesheets. Gating them on `submits_timesheets` hid exactly the senior
              staff these three fields exist to record, which is why approval could not be
              completed. Active-only filtering still applies.
            */}
            <ResourcePicker
              id="am-sa"
              value={sa.id}
              onChange={(id, name) => setSa({ id, name })}
              placeholder="Select an SA…"
              requireAssignable={false}
              // 2026-10-22: only SA titles are listed (server-side designation filter).
              roleOnProject="Solution Architect"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="am-pm">Project Manager *</Label>
            <ResourcePicker
              id="am-pm"
              value={pm.id}
              onChange={(id, name) => setPm({ id, name })}
              placeholder="Select a PM…"
              requireAssignable={false}
              roleOnProject="Project Manager"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="am-lead">Lead Engineer</Label>
            <ResourcePicker
              id="am-lead"
              value={lead.id}
              onChange={(id, name) => setLead({ id, name })}
              placeholder="Select a lead engineer…"
              requireAssignable={false}
              // Lead Implementation Engineer is NOT designation-filtered (only SA / PM / Engineer).
              roleOnProject="Lead Implementation Engineer"
            />
          </div>
        </div>

        {requiredSummary && (
          <p
            id="am-approve-blocked"
            role="status"
            className="text-xs text-error"
          >
            {requiredSummary}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={approve.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            disabled={approve.isPending || requiredSummary !== null}
            aria-describedby={
              requiredSummary ? "am-approve-blocked" : undefined
            }
          >
            {approve.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {approve.isPending ? "Approving…" : "Approve & Provision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ApprovalModal;
